import React, {
  useCallback,
  useEffect,
  useLayoutEffect,
  useMemo,
  useRef,
  useState,
} from 'react'
import { flushSync } from 'react-dom'
import { Routes, Route, useLocation, useNavigate } from 'react-router-dom'
import {
  useChartingBeforeUnload,
  useChartingHistoryLeaveGuard,
  useChartingLeaveClickGuard,
} from './charting-leave-blocker.hook'
import type { VoiceTranscriptSource } from '@hooks/useVoiceTranscriptFeed'
import type { TreatmentPlanNavigationHandle } from '@components/pages/Charting/TreatmentPlan/treatment-plan-navigation.types'

import ChartingMain from '@components/pages/Charting/Dental/ChartingMain.component'
import { type EmbeddedXraySelectedFile } from '@components/common/EmbeddedXrayViewer'
import { preloadChartingTabAssets } from './charting-tab-preload.utils'
import PerioPage from './Perio.page'
import PerioVoiceStatus from '@components/pages/Charting/Perio/PerioVoiceStatus.component'
import DentalVoiceStatus from '@components/pages/Charting/Dental/DentalVoiceStatus.component'
import TreatmentPlanPage from './TreatmentPlan.page'
import HistoryPage from './History.page'
import {
  PerioChartSectionData,
  PerioSection,
} from '@dataTypes/chartingFeature.types'
import './Charting.css'
import {
  getChartingRouteTabFromPathname,
  resolveChartingNoteOverlayPayload,
  type ChartingRouteTab,
} from './chartingNoteOverlay.util'
import { shouldMountPerioTable } from './charting-tab-mount.utils'
import { EmptyState, LinkButton, Modal } from '@enamelai/design-system'
import { Icon as ArrowLeftIcon } from '@enamelai/design-system/icons/Arrow_2_left'
import ChartingTopBar from './ChartingTopBar'
import ChartingSkeleton from './ChartingSkeleton.page'
import ChartingUnsavedChangesModal from '@components/pages/Charting/Modals/ChartingUnsavedChangesModal.component'
import {
  formatDentalDataSummary,
  type DentalDataSummary,
} from '@utils/dental-data-summary.utils'
import DentalChartSummaryContent from '@components/pages/Charting/Dental/DentalChartSummaryContent.component'
import { useActiveDentalChart } from '@hooks/useActiveDentalChart'
import { useActivePerioChart } from '@hooks/useActivePerioChart'
import { usePerioStreamingVoice } from '@hooks/usePerioStreamingVoice'
import { useDentalStreamingVoice } from '@hooks/useDentalStreamingVoice'
import { createEmptyPerioChartData } from '@utils/perio.utils'
import type { VoiceCursor } from '@utils/perio-keyword.utils'
import { useAppDispatch, useAppSelector } from '@store/hooks'
import { useStore } from 'react-redux'
import type { RootState } from '@store/index'
import {
  fetchAllPatients,
  fetchChartingbyPatientId,
  fetchInsurancePlansByPatientId,
} from '@store/actions'
import {
  setCompareCharts,
  setCreateDentalChart,
  setCreatePerioChart,
  setDentalUpdated,
  setPerioUpdated,
  setPreferCreateDentalChart,
  setPreferCreatePerioChart,
  setSelectedDentalChart,
  setSelectedPerioChart,
  clearAllChartingData,
  resetPerioChart,
  resetDentalChart,
} from '@store/reducers/charting.reducer'
import { GridComponent } from '@syncfusion/ej2-react-grids'
import {
  EmptyDentalChart,
  EmptyPerioChart,
  tabButtons,
} from '@constants/Charting'
import { setSelectedPatient } from '@store/reducers/patientmanagement.reducer'
import { readPersistedPatientSelection } from '@utils/patientSelectionStorage.util'
import {
  toggleXrayPanel,
  setXrayPanelOpen,
  requestPatientSelection,
} from '@store/reducers/uiSlice.reducer'
import { useClinicProviders } from '@hooks'
import {
  prefetchClinicChartingPreferences,
  useClinicChartingPreferences,
} from '@hooks/useClinicChartingPreferences'
import {
  defaultChartingViewToLayers,
  type ChartingLayersState,
} from '@utils/default-charting-view.utils'
import { ChartingLayerKey } from '@dataTypes/chartingPreferences.type'
import { useNotificationToast } from '@context/NotificationToastContext'
import { createFailedToast } from '@utils/notificationToast.util'
import { useGlobalLoadingActions } from '@hooks/useGlobalLoading'
import {
  useNoteEditorOverlay,
  getBackButtonLabelForOrigin,
} from '@components/common/NoteEditorOverlay'
import { useChartAutoSave } from '@hooks/useChartAutoSave'
import { useTxPlanAutoCompleteOnChartSave } from '@hooks/useTxPlanAutoCompleteOnChartSave'
import {
  CHART_DENTAL_CONTENT_REQUIRED_ERROR,
  CHART_PATIENT_REQUIRED_ERROR,
  CHART_PERIO_CONTENT_REQUIRED_ERROR,
  CHART_PROVIDER_INVALID_ERROR,
  CHART_PROVIDER_REQUIRED_ERROR,
  CHART_TITLE_REQUIRED_ERROR,
  buildManualSaveValidationToastMessage,
  hasChartingInformationFieldErrors,
} from '@utils/chart-validation.utils'
import { useChartPersist } from './useChartPersist'
import { isAlignedPersistedDentalEditSession } from '@utils/active-dental-chart.utils'
import { isAlignedPersistedPerioEditSession } from '@utils/active-perio-chart.utils'
import {
  getUniqueChartTitleResolution,
  hasDentalChartableContent,
  hasPerioChartableContent,
  type UniqueChartTitleResolution,
} from '@utils/chart-save.utils'
import { clearChartHistorySelectionClasses } from '@utils/chart-history-selection.utils'
import {
  FIRST_LOAD_OVERLAY_MS,
  FIRST_LOAD_OVERLAY_STYLE,
  waitAtLeast,
} from '@utils/waitAtLeast.util'

interface PendingSaveConfirmation {
  type: 'dental' | 'perio'
  chartId: string
  titleResolution: UniqueChartTitleResolution
  dentalSummary?: DentalDataSummary
}

type PendingChartingLeave = {
  path: string
  state: Record<string, unknown> | null
}

const GLOBAL_LOADING_CONTAINER = 'body'
const CHARTING_LOADING_CONTAINER = '.charting-loading-container'
const TAB_SKELETON_DURATION_MS = 500
const NO_TRANSCRIPT_HISTORY: VoiceTranscriptSource['history'] = []
const NO_TRANSCRIPT_QUEUED: string[] = []

function waitForChartingTabPaint(): Promise<void> {
  return new Promise((resolve) => {
    requestAnimationFrame(() => {
      requestAnimationFrame(() => {
        resolve()
      })
    })
  })
}

function waitMs(durationMs: number): Promise<void> {
  return new Promise((resolve) => {
    window.setTimeout(resolve, durationMs)
  })
}

const ChartingPage: React.FC = () => {
  useClinicProviders()
  const location = useLocation()
  const chartingNotesBackLabel = useMemo(
    (): string =>
      getBackButtonLabelForOrigin({
        path: location.pathname || '/charting',
      }),
    [location.pathname],
  )
  const navigate = useNavigate()
  const dispatch = useAppDispatch()
  const reduxStore = useStore<RootState>()
  const { showToast } = useNotificationToast()
  const { showLoading, hideLoading } = useGlobalLoadingActions()
  const dentalHistoryGridRef = useRef<GridComponent>(null)
  const perioHistoryGridRef = useRef<GridComponent>(null)
  const isMountedRef = useRef<boolean>(true)
  const tryAutoSaveBeforePromptRef = useRef<() => Promise<boolean>>(
    async () => false,
  )
  const autoSaveCurrentTabRef = useRef<() => Promise<boolean>>(
    async () => false,
  )
  const autoSaveEnabledRef = useRef(false)

  const selectedPatient = useAppSelector(
    (state) => state.patient.selectedPatient,
  )
  // Used to resolve a notification deep-link's `patientId` to the full
  // Patient record needed by `setSelectedPatient`. Empty array fallback
  // mirrors the Notes page so the lookup is a no-op until patients land.
  const patientList = useAppSelector((state) => state.patient.data ?? [])
  const patientListLoading = useAppSelector((state) => state.patient.loading)
  const selectedPerioChart = useAppSelector(
    (state) => state.charting.selectedPerioChart,
  )
  const createPerioChart = useAppSelector(
    (state) => state.charting.createPerioChart,
  )
  const createDentalChart = useAppSelector(
    (state) => state.charting.createDentalChart,
  )
  const selectedDentalChart = useAppSelector(
    (state) => state.charting.selectedDentalChart,
  )
  const dentalCharts = useAppSelector(
    (state) => state.charting.dentalCharts ?? [],
  )
  const perioCharts = useAppSelector(
    (state) => state.charting.perioCharts ?? [],
  )
  const treatmentPlans = useAppSelector(
    (state) => state.charting.treatmentPlans,
  )
  const selectedClinic = useAppSelector((state) => state.clinic.selectedClinic)
  const chartingLoading = useAppSelector((state) => state.charting.loading)
  const isDentalUpdated = useAppSelector(
    (state) => state.charting.isDentalUpdated,
  )
  const isPerioUpdated = useAppSelector(
    (state) => state.charting.isPerioUpdated,
  )
  const {
    chartingSystem,
    isReady: chartingPreferencesReady,
    autoMarkCompletedWhenCharted,
  } = useClinicChartingPreferences()
  const {
    activeDentalChart,
    commitActiveDentalChartUpdate,
    isExplicitHistorySelection: isExplicitDentalHistorySelection,
  } = useActiveDentalChart()
  const {
    activePerioChart,
    commitActivePerioChartUpdate,
    isExplicitHistorySelection,
  } = useActivePerioChart()

  const chartingTab: ChartingRouteTab = useMemo(
    (): ChartingRouteTab => getChartingRouteTabFromPathname(location.pathname),
    [location.pathname],
  )

  const isPerioLocked = useAppSelector((state) => state.charting.isPerioLocked)
  const [dentalVoiceTooth, setDentalVoiceTooth] = useState<number | undefined>()
  const perioVoiceEnabled =
    chartingTab === 'perio' &&
    Boolean(selectedPatient?.patient_id) &&
    !isPerioLocked &&
    !isExplicitHistorySelection &&
    !chartingLoading
  const dentalVoiceEnabled =
    chartingTab === 'home' &&
    Boolean(selectedPatient?.patient_id) &&
    !isExplicitDentalHistorySelection &&
    !chartingLoading
  const voice = usePerioStreamingVoice({
    enabled: perioVoiceEnabled,
    contextKey: `${selectedClinic?.clinic_id}:${selectedPatient?.patient_id}:${activePerioChart.chart_id}:${createPerioChart.chart_id}`,
    chart: {
      ...activePerioChart,
      patient_id: selectedPatient?.patient_id ?? '',
      clinic_id: activePerioChart.clinic_id || selectedClinic?.clinic_id || '',
    },
    onChart: (next) => {
      commitActivePerioChartUpdate(next)
      dispatch(setPerioUpdated(true))
    },
  })
  const dentalVoice = useDentalStreamingVoice({
    enabled: dentalVoiceEnabled,
    contextKey: `${selectedClinic?.clinic_id}:${selectedPatient?.patient_id}:${activeDentalChart.chart_id}:${createDentalChart.chart_id}`,
    chart: {
      ...activeDentalChart,
      patient_id: selectedPatient?.patient_id ?? '',
      clinic_id: activeDentalChart.clinic_id || selectedClinic?.clinic_id || '',
    },
    onChart: (next) => {
      commitActiveDentalChartUpdate(next)
      dispatch(setDentalUpdated(true))
    },
    onToothChange: (tooth) => {
      setDentalVoiceTooth(tooth)
    },
  })
  const selectPerioVoiceCursor = voice.selectCursor
  const selectDentalVoiceCursor = dentalVoice.selectCursor
  const selectPerioVoiceCursorRef = useRef(selectPerioVoiceCursor)
  selectPerioVoiceCursorRef.current = selectPerioVoiceCursor
  const stableSelectPerioCursor = useCallback(
    (patch: Partial<VoiceCursor>): void => {
      selectPerioVoiceCursorRef.current(patch)
    },
    [],
  )
  const replaceDentalVoiceChart = dentalVoice.replaceChart
  const handleDentalSelectedToothChange = useCallback(
    (tooth: number | null): void => {
      if (tooth != null) selectDentalVoiceCursor({ tooth })
    },
    [selectDentalVoiceCursor],
  )
  const activeVoicePhase =
    chartingTab === 'home' ? dentalVoice.phase : voice.phase
  const isMicOn = ['connecting', 'listening', 'processing'].includes(
    activeVoicePhase,
  )
  const voiceEnabled =
    chartingTab === 'home'
      ? dentalVoiceEnabled
      : chartingTab === 'perio'
        ? perioVoiceEnabled
        : false
  const activeVoiceToggle = useMemo((): (() => void) => {
    if (chartingTab === 'home') return dentalVoice.toggle
    return voice.toggle
  }, [chartingTab, dentalVoice.toggle, voice.toggle])
  const activeMicStatus = activeVoicePhase
  const dentalVoiceActive = ['connecting', 'listening', 'processing'].includes(
    dentalVoice.phase,
  )
  const perioVoiceActive = ['connecting', 'listening', 'processing'].includes(
    voice.phase,
  )
  const [forceDentalVoiceAccordion, setForceDentalVoiceAccordion] =
    useState(false)
  const [forcePerioVoiceAccordion, setForcePerioVoiceAccordion] =
    useState(false)
  const wasDentalVoiceActiveRef = useRef(false)
  const wasPerioVoiceActiveRef = useRef(false)
  const handleMicToggle = useCallback((): void => {
    if (!isMicOn) {
      if (chartingTab === 'home') {
        flushSync(() => {
          setForceDentalVoiceAccordion(true)
        })
      } else if (chartingTab === 'perio') {
        flushSync(() => {
          setForcePerioVoiceAccordion(true)
        })
      }
    } else if (chartingTab === 'home') {
      setForceDentalVoiceAccordion(false)
    } else if (chartingTab === 'perio') {
      setForcePerioVoiceAccordion(false)
    }
    activeVoiceToggle()
  }, [activeVoiceToggle, chartingTab, isMicOn])
  useEffect(() => {
    if (dentalVoiceActive) {
      wasDentalVoiceActiveRef.current = true
      setForceDentalVoiceAccordion(true)
      return
    }
    if (wasDentalVoiceActiveRef.current) {
      wasDentalVoiceActiveRef.current = false
      setForceDentalVoiceAccordion(false)
    }
  }, [dentalVoiceActive])
  useEffect(() => {
    if (perioVoiceActive) {
      wasPerioVoiceActiveRef.current = true
      setForcePerioVoiceAccordion(true)
      return
    }
    if (wasPerioVoiceActiveRef.current) {
      wasPerioVoiceActiveRef.current = false
      setForcePerioVoiceAccordion(false)
    }
  }, [perioVoiceActive])
  const perioVoiceForStatus = useMemo(
    () => ({
      phase: voice.phase,
      keyword: voice.keyword,
      backlog: voice.backlog,
      message: voice.message,
      // Transcript text is read through subscribe/getSnapshot.
      history: NO_TRANSCRIPT_HISTORY,
      liveText: '',
      queued: NO_TRANSCRIPT_QUEUED,
      subscribe: voice.subscribe,
      getSnapshot: voice.getSnapshot,
    }),
    [
      voice.backlog,
      voice.getSnapshot,
      voice.keyword,
      voice.message,
      voice.phase,
      voice.subscribe,
    ],
  )
  const perioVoiceStatus = useMemo(
    (): React.ReactElement => <PerioVoiceStatus voice={perioVoiceForStatus} />,
    [perioVoiceForStatus],
  )
  const perioVoiceMeasurement = useMemo(
    (): React.ReactElement => (
      <select
        id="perio-voice-measurement"
        aria-label="Voice measurement"
        className="form-select form-select-sm input-container"
        value={voice.keyword.cursor.metric}
        disabled={!perioVoiceEnabled}
        onChange={(event): void => {
          stableSelectPerioCursor({
            metric: event.target.value as 'PD' | 'GM' | 'CAL' | 'Mobility',
          })
        }}
      >
        <option value="PD">Pocket depth</option>
        <option value="GM">Gingival margin</option>
        <option value="CAL">Clinical attachment level</option>
        <option value="Mobility">Mobility</option>
      </select>
    ),
    [perioVoiceEnabled, stableSelectPerioCursor, voice.keyword.cursor.metric],
  )
  const [isFullscreen, setIsFullscreen] = useState(false)
  const isXrayPanelOpen = useAppSelector((state) => state.ui.isXrayPanelOpen)
  const [chartIndex, setChartIndex] = useState(0)
  useEffect(() => {
    if (chartingTab === 'perio') {
      const visibleTooth = voice.viewCursor?.tooth ?? voice.keyword.cursor.tooth
      setChartIndex(visibleTooth <= 16 ? 0 : 1)
    }
  }, [chartingTab, voice.keyword.cursor.tooth, voice.viewCursor?.tooth])
  // Snapshot any selected file(s) passed via route state from the Files page
  // (cloud-storage path, Phase 7.2.4). Captured once in the consolidated
  // route-state effect below and forwarded to ChartingMain via prop so a
  // refresh doesn't re-trigger the load.
  const [initialEmbeddedFiles, setInitialEmbeddedFiles] = useState<
    EmbeddedXraySelectedFile[] | null
  >(null)

  // Deep-link target captured from a notification's `location.state`
  // (`{ patientId, chartId, chartType }`). The patient is selected eagerly
  // below; the chart selection is deferred to the apply effect because
  // `dentalCharts` / `perioCharts` are only populated after
  // `fetchChartingbyPatientId` resolves. A ref keeps the target alive across
  // the navigate-replace that clears `location.state`.
  const pendingChartDeepLinkRef = useRef<{
    chartId: string
    chartType: 'dental' | 'perio'
  } | null>(null)

  // Single owner of /charting route-state consumption: dispatches the X-ray
  // panel open flag, captures any incoming selected file(s) and any
  // notification chart deep-link, then clears location.state so back/forward
  // and component re-mounts can't replay it.
  useEffect(() => {
    const routeState = location.state as {
      xrayPanelOpen?: boolean
      selectedFile?: EmbeddedXraySelectedFile
      selectedFiles?: EmbeddedXraySelectedFile[]
      // Chart notification deep-link (see charting-notifications.service.ts).
      patientId?: string
      chartId?: string
      chartType?: 'dental' | 'perio'
    } | null
    if (!routeState) return
    const hasFiles =
      (routeState.selectedFiles && routeState.selectedFiles.length > 0) ||
      Boolean(routeState.selectedFile)
    const hasChartDeepLink = Boolean(routeState.chartId && routeState.chartType)
    if (!routeState.xrayPanelOpen && !hasFiles && !hasChartDeepLink) return

    if (routeState.xrayPanelOpen === true) {
      dispatch(setXrayPanelOpen(true))
    }
    if (hasFiles) {
      const next =
        routeState.selectedFiles && routeState.selectedFiles.length > 0
          ? routeState.selectedFiles
          : routeState.selectedFile
            ? [routeState.selectedFile]
            : null
      setInitialEmbeddedFiles(next)
    }

    if (hasChartDeepLink) {
      // Stash the chart target before clearing location.state. We can't apply
      // it yet â€” the relevant charts array won't exist until the patient
      // switch (below) triggers `fetchChartingbyPatientId`.
      pendingChartDeepLinkRef.current = {
        chartId: routeState.chartId as string,
        chartType: routeState.chartType as 'dental' | 'perio',
      }
      // Switch patient now if the notification targets a different one and
      // we have it in the Redux patient list. If the list hasn't loaded yet
      // (cold open), the apply effect below will short-circuit until charts
      // exist, and the existing patient-fetch effect on `selectedPatient`
      // will run as soon as the dispatch lands.
      if (
        routeState.patientId &&
        routeState.patientId !== selectedPatient?.patient_id
      ) {
        const target = patientList.find(
          (p) => p.patient_id === routeState.patientId,
        )
        if (target) {
          dispatch(setSelectedPatient({ patient: target }))
        }
      }
    }

    navigate(location.pathname, { replace: true, state: null })
  }, [
    dispatch,
    location.state,
    location.pathname,
    navigate,
    patientList,
    selectedPatient?.patient_id,
  ])

  // When the active patient is removed (e.g. via the header "Remove" action,
  // which nulls `selectedPatient`), drop any previously-loaded X-ray file(s)
  // so the embedded viewer returns to its empty state instead of holding on
  // to the prior patient's DICOM.
  useEffect(() => {
    if (!selectedPatient?.patient_id) {
      setInitialEmbeddedFiles(null)
    }
  }, [selectedPatient?.patient_id])

  const [showSaveConfirm, setShowSaveConfirm] = useState(false)
  const [pendingSaveData, setPendingSaveData] =
    useState<PendingSaveConfirmation | null>(null)
  const [perioValidationErrors, setPerioValidationErrors] = useState<{
    [key: string]: string
  }>({})
  const [dentalValidationErrors, setDentalValidationErrors] = useState<{
    [key: string]: string
  }>({})
  const [dentalChartingInfoFocusTick, setDentalChartingInfoFocusTick] =
    useState(0)
  const [perioChartingInfoFocusTick, setPerioChartingInfoFocusTick] =
    useState(0)
  const [alertMessageInfo, setAlertMessageInfo] = useState<{
    title: string
    message: string
    type: 'success' | 'error'
    show: boolean
  }>({
    title: '',
    message: '',
    type: 'success',
    show: false,
  })
  const { openNoteEditor } = useNoteEditorOverlay()
  const prevClinicIdRef = useRef<string | undefined>(undefined)

  const locationState = location.state as {
    previousPage?: { path: string; backLabel?: string }
  } | null
  const pagePreviousState =
    locationState?.previousPage &&
    typeof locationState.previousPage.path === 'string'
      ? locationState.previousPage
      : null
  const chartingBackLabel =
    typeof pagePreviousState?.backLabel === 'string' &&
    pagePreviousState.backLabel.trim()
      ? pagePreviousState.backLabel
      : 'Back to appointment'
  const addTreatmentPlanRef = useRef<(() => void) | null>(null)
  const treatmentPlanNavigationRef =
    useRef<TreatmentPlanNavigationHandle | null>(null)
  const [showUnsavedLeaveConfirm, setShowUnsavedLeaveConfirm] = useState(false)
  const [pendingLeave, setPendingLeave] = useState<PendingChartingLeave | null>(
    null,
  )
  const prevChartingTabRef = useRef<ChartingRouteTab | null>(null)
  const prevBootstrapPatientIdRef = useRef<string | null>(null)
  const prevBootstrapClinicIdRef = useRef<string | undefined>(undefined)
  const patientFetchAttemptClinicIdRef = useRef<string | null>(null)
  const [
    patientSelectionResolvedClinicId,
    setPatientSelectionResolvedClinicId,
  ] = useState<string | null>(null)
  const [isPageReady, setIsPageReady] = useState(false)
  const isPageReadyRef = useRef(false)
  const [dentalChartAssetsReady, setDentalChartAssetsReady] = useState(false)
  const [tabTransitionReady, setTabTransitionReady] = useState(true)
  // Keep dental mounted on dental/perio/history; unmount only on plan tab so treatment
  // plan ToothChart is the only live dental chart instance (shared SVG ids).
  const [dentalChartMounted, setDentalChartMounted] = useState(
    (): boolean =>
      getChartingRouteTabFromPathname(location.pathname) !== 'treatment-plan',
  )
  const perioTableVisitedRef = useRef(
    getChartingRouteTabFromPathname(location.pathname) === 'perio',
  )
  if (chartingTab === 'perio') {
    perioTableVisitedRef.current = true
  }

  const clinicId = selectedClinic?.clinic_id
  const persistedPatientId =
    readPersistedPatientSelection(clinicId)?.selectedPatientId ?? null
  const isRestoringPersistedPatient =
    !selectedPatient?.patient_id &&
    Boolean(clinicId) &&
    Boolean(persistedPatientId) &&
    patientSelectionResolvedClinicId !== clinicId

  const isPatientBootstrapPending =
    Boolean(selectedPatient?.patient_id) &&
    (chartingLoading || !dentalChartAssetsReady)

  const isTabTransitionPending =
    Boolean(selectedPatient?.patient_id) && !tabTransitionReady

  const isChartContentPending =
    isRestoringPersistedPatient ||
    isPatientBootstrapPending ||
    isTabTransitionPending

  const showChartingSkeleton = isChartContentPending

  useEffect(() => {
    if (!clinicId) {
      setPatientSelectionResolvedClinicId(null)
      return
    }

    if (selectedPatient?.patient_id) {
      setPatientSelectionResolvedClinicId(clinicId)
      return
    }

    const storedPatientId =
      readPersistedPatientSelection(clinicId)?.selectedPatientId ?? null
    if (!storedPatientId) {
      setPatientSelectionResolvedClinicId(clinicId)
      return
    }

    if (patientListLoading) {
      return
    }

    if (
      patientList.length === 0 &&
      patientFetchAttemptClinicIdRef.current !== clinicId
    ) {
      patientFetchAttemptClinicIdRef.current = clinicId
      void dispatch(fetchAllPatients())
      return
    }

    const restoredPatient = patientList.find(
      (patient) => patient.patient_id === storedPatientId,
    )
    if (restoredPatient) {
      dispatch(setSelectedPatient({ patient: restoredPatient }))
    }

    setPatientSelectionResolvedClinicId(clinicId)
  }, [
    clinicId,
    dispatch,
    patientList,
    patientListLoading,
    selectedPatient?.patient_id,
  ])

  useEffect(() => {
    if (!tabTransitionReady) {
      return
    }
    setDentalChartMounted(chartingTab !== 'treatment-plan')
  }, [chartingTab, tabTransitionReady])

  // On clinic change: show global loading, fetch patients for new clinic, then show Select Patient modal
  useEffect(() => {
    const resolvedClinicId =
      clinicId ||
      (typeof localStorage !== 'undefined'
        ? localStorage.getItem('clinic_id') || ''
        : '')
    if (resolvedClinicId) {
      void prefetchClinicChartingPreferences(resolvedClinicId)
    }
  }, [clinicId])

  useEffect(() => {
    const prevClinicId = prevClinicIdRef.current
    if (prevClinicId !== undefined && prevClinicId !== clinicId) {
      showLoading({ container: GLOBAL_LOADING_CONTAINER })
      dispatch(fetchAllPatients())
        .unwrap()
        .finally(() => {
          hideLoading(GLOBAL_LOADING_CONTAINER)
        })
    }
    prevClinicIdRef.current = clinicId
  }, [clinicId, dispatch, showLoading, hideLoading])

  useLayoutEffect(() => {
    if (isPageReady || !selectedPatient?.patient_id) {
      hideLoading(CHARTING_LOADING_CONTAINER)
      return
    }
    showLoading({
      container: CHARTING_LOADING_CONTAINER,
      style: FIRST_LOAD_OVERLAY_STYLE,
    })
  }, [hideLoading, isPageReady, selectedPatient?.patient_id, showLoading])

  useEffect(() => {
    let cancelled = false
    isMountedRef.current = true
    const startedAt = Date.now()

    const patientId = selectedPatient?.patient_id ?? ''
    const bootstrapClinicId = selectedClinic?.clinic_id ?? ''
    const previousPatientId = prevBootstrapPatientIdRef.current
    const previousClinicId = prevBootstrapClinicIdRef.current
    const patientChanged = previousPatientId !== patientId
    const clinicChanged = previousClinicId !== bootstrapClinicId
    prevBootstrapPatientIdRef.current = patientId
    prevBootstrapClinicIdRef.current = bootstrapClinicId

    const finishOverlay = async (): Promise<void> => {
      await waitAtLeast(startedAt, FIRST_LOAD_OVERLAY_MS)
      if (cancelled || !isMountedRef.current) return
      setDentalChartAssetsReady(true)
      setTabTransitionReady(true)
      isPageReadyRef.current = true
      setIsPageReady(true)
      hideLoading(CHARTING_LOADING_CONTAINER)
    }

    if (selectedPatient) {
      if (selectedDentalChart && selectedDentalChart.patient_id !== patientId) {
        dispatch(setSelectedDentalChart(null))
      }
      if (selectedPerioChart && selectedPerioChart.patient_id !== patientId) {
        dispatch(setSelectedPerioChart(null))
      }
    }

    if (patientId && (patientChanged || !isPageReadyRef.current)) {
      const keepVisibleSkeleton = isPageReadyRef.current
      if (!keepVisibleSkeleton) {
        isPageReadyRef.current = false
        setIsPageReady(false)
        showLoading({
          container: CHARTING_LOADING_CONTAINER,
          style: FIRST_LOAD_OVERLAY_STYLE,
        })
      }
      setDentalChartAssetsReady(false)
      setTabTransitionReady(false)
      Promise.all([
        dispatch(fetchChartingbyPatientId(patientId)).unwrap(),
        dispatch(fetchInsurancePlansByPatientId(patientId)).unwrap(),
        preloadChartingTabAssets(chartingTab),
      ]).finally(() => {
        void (async (): Promise<void> => {
          if (cancelled || !isMountedRef.current) return
          await finishOverlay()
          if (cancelled || !isMountedRef.current) return
          prevChartingTabRef.current = chartingTab
        })()
      })
      setPerioValidationErrors((prevErrors) => {
        const updatedErrors = { ...prevErrors }
        delete updatedErrors['patient_id']
        return updatedErrors
      })
      setDentalValidationErrors((prevErrors) => {
        const updatedErrors = { ...prevErrors }
        delete updatedErrors['patient_id']
        return updatedErrors
      })
      dispatch(
        setCreatePerioChart({
          ...EmptyPerioChart,
          patient_id: patientId,
          clinic_id: bootstrapClinicId,
        }),
      )
      dispatch(
        setCreateDentalChart({
          ...EmptyDentalChart,
          patient_id: patientId,
          clinic_id: bootstrapClinicId,
        }),
      )
      dispatch(setPreferCreateDentalChart(false))
      dispatch(setPreferCreatePerioChart(false))
      dispatch(setDentalUpdated(false))
      dispatch(setPerioUpdated(false))
      dispatch(setCompareCharts(null))
      setPerioValidationErrors({})
      setDentalValidationErrors({})
    } else if (!patientId && (patientChanged || !isPageReadyRef.current)) {
      if (patientChanged && previousPatientId) {
        setDentalChartAssetsReady(false)
        setTabTransitionReady(true)
        prevChartingTabRef.current = null
        dispatch(clearAllChartingData())
        const historyGridRefs = [dentalHistoryGridRef, perioHistoryGridRef]
        historyGridRefs.forEach((historyGridRef) => {
          if (!isMountedRef.current || !historyGridRef.current) {
            return
          }
          try {
            const gridElement = historyGridRef.current.element
            if (gridElement && gridElement.parentNode) {
              historyGridRef.current.dataSource = []
            }
          } catch (error) {
            // eslint-disable-next-line no-console
            console.warn('Error clearing grid data source:', error)
          }
        })
      }
      isPageReadyRef.current = true
      setIsPageReady(true)
      setTabTransitionReady(true)
      hideLoading(CHARTING_LOADING_CONTAINER)
    } else if (
      patientId &&
      clinicChanged &&
      !patientChanged &&
      bootstrapClinicId
    ) {
      if (createPerioChart.clinic_id !== bootstrapClinicId) {
        dispatch(
          setCreatePerioChart({
            ...createPerioChart,
            clinic_id: bootstrapClinicId,
          }),
        )
      }
      if (createDentalChart.clinic_id !== bootstrapClinicId) {
        dispatch(
          setCreateDentalChart({
            ...createDentalChart,
            clinic_id: bootstrapClinicId,
          }),
        )
      }
    }

    return (): void => {
      cancelled = true
      isMountedRef.current = false
    }
    // chartingTab: preload at patient select only; tab switches use the effect below
    // eslint-disable-next-line react-hooks/exhaustive-deps -- bootstrap only when patient/clinic id changes
  }, [
    selectedPatient?.patient_id,
    selectedClinic?.clinic_id,
    dispatch,
    showLoading,
    hideLoading,
  ])

  useEffect(() => {
    return () => {
      hideLoading(CHARTING_LOADING_CONTAINER)
    }
  }, [hideLoading])

  // Apply a pending notification chart deep-link once the relevant charts
  // array has been populated for the current patient. Runs at most once per
  // captured target â€” the ref is cleared after dispatching the setter (or
  // after we determine the target chart doesn't exist for this patient).
  // Idempotent: re-runs are guarded by `pendingChartDeepLinkRef.current`.
  useEffect(() => {
    const target = pendingChartDeepLinkRef.current
    if (!target) return
    if (!selectedPatient?.patient_id) return
    // Wait until the patient-bootstrap fetch completes so charts arrays are
    // authoritative for this patient.
    if (isPatientBootstrapPending) return

    if (target.chartType === 'dental') {
      const match = dentalCharts.find((c) => c.chart_id === target.chartId)
      pendingChartDeepLinkRef.current = null
      if (match && match.patient_id === selectedPatient.patient_id) {
        dispatch(setSelectedDentalChart(match))
      }
    } else {
      // The perio notification's actionUrl is `/charting/perio`, which renders
      // `PerioPage` directly off `selectedPerioChart` â€” no layer toggle is
      // needed here (that's only for the dental "home" tab's perio overlay).
      const match = perioCharts.find((c) => c.chart_id === target.chartId)
      pendingChartDeepLinkRef.current = null
      if (match && match.patient_id === selectedPatient.patient_id) {
        dispatch(setSelectedPerioChart(match))
      }
    }
  }, [
    dispatch,
    selectedPatient?.patient_id,
    isPatientBootstrapPending,
    dentalCharts,
    perioCharts,
  ])

  const ensureSectionData = (
    data?: PerioChartSectionData,
  ): PerioChartSectionData => {
    const empty = createEmptyPerioChartData().facial
    return {
      PD: data?.PD ?? empty.PD,
      GM: data?.GM ?? empty.GM,
      CAL: data?.CAL ?? empty.CAL,
      Bleeding: data?.Bleeding ?? empty.Bleeding,
      Calculus: data?.Calculus ?? empty.Calculus,
      Supp: data?.Supp ?? empty.Supp,
      Furcation: data?.Furcation ?? empty.Furcation,
      Mobility: data?.Mobility ?? empty.Mobility,
    }
  }

  useEffect(() => {
    const patientId = selectedPatient?.patient_id
    if (!patientId) {
      return
    }

    if (isPatientBootstrapPending) {
      return
    }

    const previousTab = prevChartingTabRef.current
    if (previousTab === chartingTab) {
      return
    }

    let cancelled = false
    setTabTransitionReady(false)

    void preloadChartingTabAssets(chartingTab).finally(() => {
      if (cancelled || !isMountedRef.current) {
        return
      }
      prevChartingTabRef.current = chartingTab
      void Promise.all([
        waitForChartingTabPaint(),
        waitMs(TAB_SKELETON_DURATION_MS),
      ]).then(() => {
        if (!cancelled && isMountedRef.current) {
          setTabTransitionReady(true)
        }
      })
    })

    return (): void => {
      cancelled = true
    }
  }, [chartingTab, selectedPatient?.patient_id, isPatientBootstrapPending])

  const [layers, setLayers] = useState<ChartingLayersState>(() =>
    defaultChartingViewToLayers(undefined),
  )
  const layersInitializedRef = useRef(false)
  const layersClinicIdRef = useRef<string | undefined>(undefined)

  useEffect(() => {
    if (!chartingPreferencesReady) {
      return
    }

    const shouldApplyDefaults =
      !layersInitializedRef.current || layersClinicIdRef.current !== clinicId

    if (!shouldApplyDefaults) {
      return
    }

    setLayers(defaultChartingViewToLayers(chartingSystem.default_charting_view))
    layersInitializedRef.current = true
    layersClinicIdRef.current = clinicId
  }, [chartingPreferencesReady, chartingSystem.default_charting_view, clinicId])

  const handleLayerChange = (layer: ChartingLayerKey): void => {
    setLayers((prev) => ({
      ...prev,
      [layer]: !prev[layer],
    }))
  }

  const handleChartToggle = (direction: 'left' | 'right'): void => {
    setChartIndex(direction === 'left' ? 0 : 1)
    const isUpperArch = voice.keyword.cursor.tooth <= 16
    if (chartingTab === 'perio' && isUpperArch !== (direction === 'left'))
      voice.selectCursor({ tooth: direction === 'left' ? 1 : 17 })
  }

  const hasTreatmentPlanUnsaved = useCallback((): boolean => {
    return treatmentPlanNavigationRef.current?.hasUnsavedChanges() === true
  }, [])

  // Dental, perio, and treatment plan each keep unsaved work across tab switches,
  // so leaving charting checks all three regardless of the current tab.
  const hasAnyUnsavedForLeaveGuard = useCallback((): boolean => {
    const { isDentalUpdated: dentalDirty, isPerioUpdated: perioDirty } =
      reduxStore.getState().charting
    return dentalDirty || perioDirty || hasTreatmentPlanUnsaved()
  }, [hasTreatmentPlanUnsaved, reduxStore])

  const getUnsavedTabLabels = (): string[] => {
    const unsaved: Record<string, boolean> = {
      home: isDentalUpdated,
      perio: isPerioUpdated,
      'treatment-plan': hasTreatmentPlanUnsaved(),
    }
    return tabButtons
      .filter((button) => unsaved[button.value])
      .map((button) => button.label)
  }

  const promptToLeaveRef = useRef<
    (targetPath: string, state: Record<string, unknown> | null) => void
  >(() => undefined)
  const allowNextChartingLeave = useChartingHistoryLeaveGuard(
    () => hasAnyUnsavedForLeaveGuard(),
    (targetPath) => promptToLeaveRef.current(targetPath, null),
  )
  useChartingLeaveClickGuard(
    () => hasAnyUnsavedForLeaveGuard(),
    (targetPath) => promptToLeaveRef.current(targetPath, null),
  )
  useChartingBeforeUnload(() => hasAnyUnsavedForLeaveGuard())

  const leaveChartingTo = useCallback(
    (targetPath: string, state: Record<string, unknown> | null): void => {
      allowNextChartingLeave()
      navigate(targetPath, state ? { state } : undefined)
    },
    [allowNextChartingLeave, navigate],
  )

  const promptToLeave = useCallback(
    (targetPath: string, state: Record<string, unknown> | null): void => {
      if (!autoSaveEnabledRef.current) {
        setPendingLeave({ path: targetPath, state })
        setShowUnsavedLeaveConfirm(true)
        return
      }
      void tryAutoSaveBeforePromptRef.current().then((saved) => {
        if (saved) {
          leaveChartingTo(targetPath, state)
          return
        }
        setPendingLeave({ path: targetPath, state })
        setShowUnsavedLeaveConfirm(true)
      })
    },
    [leaveChartingTo],
  )
  promptToLeaveRef.current = promptToLeave

  const performTabNavigation = useCallback(
    (tab: string): void => {
      if (
        tab === 'home' ||
        tab === 'perio' ||
        tab === 'treatment-plan' ||
        tab === 'history'
      ) {
        if (selectedPatient?.patient_id && tab !== chartingTab) {
          flushSync(() => {
            setTabTransitionReady(false)
          })
        }
        navigate(tab === 'home' ? '/charting' : `/charting/${tab}`)
      }
    },
    [navigate, selectedPatient?.patient_id, chartingTab],
  )

  const handleChangeTab = useCallback(
    (tab: string): void => {
      // Autosave timers follow the current tab, so flush them before it changes.
      if (tab !== chartingTab && autoSaveEnabledRef.current) {
        void autoSaveCurrentTabRef.current()
      }
      performTabNavigation(tab)
    },
    [chartingTab, performTabNavigation],
  )

  const handleGoToPlans = useCallback((): void => {
    handleChangeTab('treatment-plan')
  }, [handleChangeTab])

  const completePendingLeave = useCallback((): void => {
    const pending = pendingLeave
    setPendingLeave(null)
    setShowUnsavedLeaveConfirm(false)
    if (!pending) {
      return
    }
    leaveChartingTo(pending.path, pending.state)
  }, [pendingLeave, leaveChartingTo])

  const requestLeavePage = useCallback(
    (targetPath: string, state?: Record<string, unknown>): void => {
      if (!hasAnyUnsavedForLeaveGuard()) {
        navigate(targetPath, state ? { state } : undefined)
        return
      }
      promptToLeave(targetPath, state ?? null)
    },
    [hasAnyUnsavedForLeaveGuard, navigate, promptToLeave],
  )

  const handleGoBack = useCallback((): void => {
    if (!pagePreviousState) {
      return
    }
    requestLeavePage(pagePreviousState.path, { ...pagePreviousState })
  }, [pagePreviousState, requestLeavePage])

  const handleOpenChartingNotesOverlay = (
    id: string | null,
    openFirstNote?: boolean,
  ): void => {
    if (!selectedPatient) {
      showToast(createFailedToast('Notes', 'Please select a patient first'))
      return
    }
    const payload = resolveChartingNoteOverlayPayload({
      currentTab: chartingTab,
      id,
      openFirstNote,
      treatmentPlans,
      dentalCharts,
      perioCharts,
      selectedDentalChart,
      createDentalChart,
      activeDentalChart,
      selectedPerioChart,
      createPerioChart,
    })
    openNoteEditor({
      patient: selectedPatient,
      contextId: payload.contextId ?? null,
      contextType: payload.contextType ?? null,
      contextName: payload.contextName ?? null,
      openFirstNote: payload.openFirstNote ?? false,
      editorBackButtonLabel: chartingNotesBackLabel,
      openContextNotesByDefault: true,
      overlayBackLabel: chartingNotesBackLabel,
      initialView: payload.openFirstNote ? 'list' : 'add',
      anchorClassName: 'charting-note-editor-overlay-anchor',
      notesFetchLoadingContainer: 'body',
      portalTarget: '.page-content',
      pagePreviousState,
    })
  }
  const handleOpenChartingNotesOverlayRef = useRef(
    handleOpenChartingNotesOverlay,
  )
  handleOpenChartingNotesOverlayRef.current = handleOpenChartingNotesOverlay
  const stableOnViewNote = useCallback(
    (id: string | null, openFirstNote?: boolean): void => {
      handleOpenChartingNotesOverlayRef.current(id, openFirstNote)
    },
    [],
  )

  const updatePerioValue = (
    section: PerioSection,
    label: keyof PerioChartSectionData,
    toothIndex: number,
    newValue: string,
  ): void => {
    const facial = ensureSectionData(activePerioChart?.perio_data?.facial)
    const lingual = ensureSectionData(activePerioChart?.perio_data?.lingual)
    const updatedSection = section === PerioSection.Facial ? facial : lingual
    const updatedValues = [...updatedSection[label]]
    updatedValues[toothIndex] = newValue

    const updatedData: PerioChartSectionData = {
      ...updatedSection,
      [label]: updatedValues,
    }
    const chartNewValue = {
      ...activePerioChart,
      chart_id: activePerioChart?.chart_id ?? '',
      provider_id: activePerioChart?.provider_id ?? '',
      patient_id: activePerioChart?.patient_id ?? '',
      title: activePerioChart?.title ?? '',
      description: activePerioChart?.description ?? '',
      perio_data: {
        facial: section === PerioSection.Facial ? updatedData : facial,
        lingual: section === PerioSection.Lingual ? updatedData : lingual,
      },
    }
    commitActivePerioChartUpdate(chartNewValue)
    dispatch(setPerioUpdated(true))
  }
  const updatePerioValueRef = useRef(updatePerioValue)
  updatePerioValueRef.current = updatePerioValue
  const stableUpdatePerioValue = useCallback(
    (
      section: PerioSection,
      label: keyof PerioChartSectionData,
      toothIndex: number,
      newValue: string,
    ): void => {
      updatePerioValueRef.current(section, label, toothIndex, newValue)
    },
    [],
  )

  const clearHistorySelection = useCallback((tab: ChartingRouteTab): void => {
    const historyGrid =
      tab === 'perio'
        ? perioHistoryGridRef.current
        : dentalHistoryGridRef.current
    if (isMountedRef.current && historyGrid) {
      try {
        const gridElement = historyGrid.element
        if (gridElement && gridElement.parentNode) {
          clearChartHistorySelectionClasses(historyGrid)
          historyGrid.clearSelection()
        }
      } catch (error) {
        // eslint-disable-next-line no-console
        console.warn('Error clearing grid selection:', error)
      }
    }
  }, [])
  const clearSelection = useCallback((): void => {
    clearHistorySelection(chartingTab)
  }, [chartingTab, clearHistorySelection])
  const handleAddChart = (): void => {
    if (chartingTab === 'perio')
      selectPerioVoiceCursor({
        tooth: 1,
        section: PerioSection.Facial,
        metric: 'PD',
      })
    // Data consistency validation: ensure patient and clinic match
    if (selectedPatient && selectedClinic) {
      if (selectedPatient.clinic_id !== selectedClinic.clinic_id) {
        setAlertMessageInfo({
          title: 'Clinic Mismatch',
          message:
            'The selected patient does not belong to the selected clinic. Please select a patient from the current clinic.',
          type: 'error',
          show: true,
        })
        return
      }
    }

    if (chartingTab === 'perio') {
      setPerioValidationErrors({})
      dispatch(setSelectedPerioChart(null))
      dispatch(setPreferCreatePerioChart(true))
      dispatch(
        setCreatePerioChart({
          ...EmptyPerioChart,
          patient_id: selectedPatient?.patient_id ?? '',
          clinic_id: selectedClinic?.clinic_id ?? '',
        }),
      )
    } else if (chartingTab === 'home') {
      setDentalValidationErrors({})
      dispatch(setSelectedDentalChart(null))
      dispatch(setPreferCreateDentalChart(true))
      dispatch(
        setCreateDentalChart({
          ...EmptyDentalChart,
          patient_id: selectedPatient?.patient_id ?? '',
          clinic_id: selectedClinic?.clinic_id ?? '',
        }),
      )
    }
    dispatch(setCompareCharts(null))
    if (chartingTab === 'perio') {
      dispatch(setPerioUpdated(false))
    } else if (chartingTab === 'home') {
      dispatch(setDentalUpdated(false))
    }
    clearSelection()
  }

  const resetChartForTab = useCallback(
    (tab: ChartingRouteTab): void => {
      if (tab === 'perio')
        selectPerioVoiceCursor({
          tooth: 1,
          section: PerioSection.Facial,
          metric: 'PD',
        })
      const patientId = selectedPatient?.patient_id ?? ''
      const resetClinicId = selectedClinic?.clinic_id ?? ''
      if (tab === 'perio') {
        setPerioValidationErrors({})
        const isPerioEdit = isAlignedPersistedPerioEditSession(
          selectedPerioChart,
          createPerioChart,
        )
        if (isPerioEdit) {
          const chartId = selectedPerioChart?.chart_id?.trim() ?? ''
          const restored =
            perioCharts.find((chart) => chart.chart_id === chartId) ??
            selectedPerioChart
          flushSync(() => {
            dispatch(setSelectedPerioChart(restored))
            dispatch(
              setCreatePerioChart({
                ...EmptyPerioChart,
                patient_id: patientId,
                clinic_id: resetClinicId,
              }),
            )
            dispatch(setPreferCreatePerioChart(false))
          })
          dispatch(setPerioUpdated(false))
          return
        }
        flushSync(() => {
          dispatch(resetPerioChart())
        })
        dispatch(setPerioUpdated(false))
      }
      if (tab === 'home') {
        setDentalValidationErrors({})
        const isDentalEdit = isAlignedPersistedDentalEditSession(
          selectedDentalChart,
          createDentalChart,
        )
        if (isDentalEdit) {
          const chartId = selectedDentalChart?.chart_id?.trim() ?? ''
          const restored =
            dentalCharts.find((chart) => chart.chart_id === chartId) ??
            selectedDentalChart
          flushSync(() => {
            dispatch(setSelectedDentalChart(restored))
            dispatch(
              setCreateDentalChart({
                ...EmptyDentalChart,
                patient_id: patientId,
                clinic_id: resetClinicId,
              }),
            )
            dispatch(setPreferCreateDentalChart(false))
          })
          if (restored) {
            replaceDentalVoiceChart({
              ...restored,
              patient_id: patientId,
              clinic_id: resetClinicId,
            })
          }
          dispatch(setDentalUpdated(false))
          return
        }
        const resetChart = {
          ...EmptyDentalChart,
          patient_id: patientId,
          clinic_id: resetClinicId,
        }
        flushSync(() => {
          dispatch(resetDentalChart())
        })
        replaceDentalVoiceChart(resetChart)
        dispatch(setDentalUpdated(false))
      }
      clearHistorySelection(tab)
    },
    [
      clearHistorySelection,
      createDentalChart,
      createPerioChart,
      dentalCharts,
      dispatch,
      perioCharts,
      selectedClinic?.clinic_id,
      selectedDentalChart,
      selectedPatient?.patient_id,
      selectedPerioChart,
      replaceDentalVoiceChart,
      selectPerioVoiceCursor,
    ],
  )

  const handleResetChart = useCallback((): void => {
    resetChartForTab(chartingTab)
  }, [chartingTab, resetChartForTab])

  const validatePerioFormForManualSave = useCallback((): boolean => {
    const isNewChart = !activePerioChart?.chart_id?.trim()
    const errors: { [key: string]: string } = {}

    if (perioValidationErrors.provider_id === CHART_PROVIDER_INVALID_ERROR) {
      errors.provider_id = CHART_PROVIDER_INVALID_ERROR
    }

    if (!activePerioChart?.patient_id && !selectedPatient?.patient_id) {
      errors.patient_id = CHART_PATIENT_REQUIRED_ERROR
    }

    if (isNewChart) {
      if (!activePerioChart?.provider_id) {
        errors.provider_id = CHART_PROVIDER_REQUIRED_ERROR
      }
      if (!activePerioChart?.title?.trim()) {
        errors.title = CHART_TITLE_REQUIRED_ERROR
      }
    }

    if (!hasPerioChartableContent(activePerioChart?.perio_data)) {
      errors.content = CHART_PERIO_CONTENT_REQUIRED_ERROR
    }

    setPerioValidationErrors(errors)
    const hasErrors = Object.keys(errors).length > 0
    if (hasErrors) {
      if (hasChartingInformationFieldErrors(errors)) {
        setPerioChartingInfoFocusTick((tick) => tick + 1)
      }
      showToast(
        createFailedToast(
          'Cannot save chart',
          buildManualSaveValidationToastMessage(errors),
        ),
      )
    }
    return !hasErrors
  }, [
    activePerioChart,
    perioValidationErrors.provider_id,
    selectedPatient?.patient_id,
    showToast,
  ])

  const validateDentalFormForManualSave = useCallback((): boolean => {
    const chartToValidate = activeDentalChart
    const isNewChart = !chartToValidate?.chart_id?.trim()
    const errors: { [key: string]: string } = {}

    if (dentalValidationErrors.provider_id === CHART_PROVIDER_INVALID_ERROR) {
      errors.provider_id = CHART_PROVIDER_INVALID_ERROR
    }

    if (!chartToValidate?.patient_id && !selectedPatient?.patient_id) {
      errors.patient_id = CHART_PATIENT_REQUIRED_ERROR
    }

    if (isNewChart) {
      if (!chartToValidate?.provider_id) {
        errors.provider_id = CHART_PROVIDER_REQUIRED_ERROR
      }
      if (!chartToValidate?.title?.trim()) {
        errors.title = CHART_TITLE_REQUIRED_ERROR
      }
    }

    if (!hasDentalChartableContent(chartToValidate?.dental_data)) {
      errors.content = CHART_DENTAL_CONTENT_REQUIRED_ERROR
    }

    setDentalValidationErrors(errors)
    const hasErrors = Object.keys(errors).length > 0
    if (hasErrors) {
      if (hasChartingInformationFieldErrors(errors)) {
        setDentalChartingInfoFocusTick((tick) => tick + 1)
      }
      showToast(
        createFailedToast(
          'Cannot save chart',
          buildManualSaveValidationToastMessage(errors),
        ),
      )
    }
    return !hasErrors
  }, [
    activeDentalChart,
    dentalValidationErrors.provider_id,
    selectedPatient?.patient_id,
    showToast,
  ])

  const validatePerioFormForAutoSave = useCallback((): boolean => {
    const errors: { [key: string]: string } = {}
    if (!createPerioChart?.patient_id && !selectedPatient?.patient_id) {
      errors.patient_id = CHART_PATIENT_REQUIRED_ERROR
    }
    setPerioValidationErrors(errors)
    return Object.keys(errors).length === 0
  }, [createPerioChart?.patient_id, selectedPatient?.patient_id])

  const validateDentalFormForAutoSave = useCallback((): boolean => {
    const errors: { [key: string]: string } = {}
    if (!activeDentalChart?.patient_id && !selectedPatient?.patient_id) {
      errors.patient_id = CHART_PATIENT_REQUIRED_ERROR
    }
    setDentalValidationErrors(errors)
    return Object.keys(errors).length === 0
  }, [activeDentalChart?.patient_id, selectedPatient?.patient_id])

  const validateForManualSave = useCallback(
    (tab: ChartingRouteTab): boolean => {
      if (tab === 'home') return validateDentalFormForManualSave()
      if (tab === 'perio') return validatePerioFormForManualSave()
      return false
    },
    [validateDentalFormForManualSave, validatePerioFormForManualSave],
  )

  const validateForAutoSave = useCallback(
    (tab: ChartingRouteTab): boolean => {
      if (tab === 'home') return validateDentalFormForAutoSave()
      if (tab === 'perio') return validatePerioFormForAutoSave()
      return false
    },
    [validateDentalFormForAutoSave, validatePerioFormForAutoSave],
  )

  const { syncTreatmentPlanCompletionAfterChartSave } =
    useTxPlanAutoCompleteOnChartSave({
      enabled: autoMarkCompletedWhenCharted,
      patientId: selectedPatient?.patient_id ?? '',
      treatmentPlans,
    })

  const {
    persistChart,
    exitEditingSession,
    dentalSignature,
    perioSignature,
    canAutoSaveDental,
    canAutoSavePerio,
    resolveDentalChartForSave,
    resolvePerioChartForSave,
  } = useChartPersist({
    validateForManualSave,
    validateForAutoSave,
    syncTreatmentPlanCompletionAfterChartSave,
  })

  const buildSaveConfirmation = useCallback(
    (type: 'dental' | 'perio'): PendingSaveConfirmation => {
      if (type === 'perio') {
        const chart = resolvePerioChartForSave()
        const chartId = chart.chart_id ?? ''
        const patientId =
          chart.patient_id !== ''
            ? chart.patient_id
            : (selectedPatient?.patient_id ?? '')
        return {
          type,
          chartId,
          titleResolution: getUniqueChartTitleResolution(
            chart.title ?? '',
            perioCharts,
            patientId,
            chartId,
          ),
        }
      }

      const chart = resolveDentalChartForSave()
      const chartId = chart.chart_id ?? ''
      const patientId =
        chart.patient_id !== ''
          ? chart.patient_id
          : (selectedPatient?.patient_id ?? '')
      return {
        type,
        chartId,
        titleResolution: getUniqueChartTitleResolution(
          chart.title ?? '',
          dentalCharts,
          patientId,
          chartId,
        ),
        dentalSummary: formatDentalDataSummary(chart.dental_data || []),
      }
    },
    [
      dentalCharts,
      perioCharts,
      resolveDentalChartForSave,
      resolvePerioChartForSave,
      selectedPatient?.patient_id,
    ],
  )

  const autoSaveEnabled =
    chartingPreferencesReady && chartingSystem.autosave_changes === 'yes'

  const isInPersistedEditSession = useMemo((): boolean => {
    if (chartingTab === 'home') {
      return isAlignedPersistedDentalEditSession(
        selectedDentalChart,
        createDentalChart,
      )
    }
    if (chartingTab === 'perio') {
      return isAlignedPersistedPerioEditSession(
        selectedPerioChart,
        createPerioChart,
      )
    }
    return false
  }, [
    chartingTab,
    createDentalChart,
    createPerioChart,
    selectedDentalChart,
    selectedPerioChart,
  ])

  const canFinishEditing = isInPersistedEditSession

  const handleAutoSave = useCallback(async (): Promise<boolean> => {
    return persistChart({
      mode: 'autosave',
      chartingTab,
      keepEditingSession: true,
      silent: true,
    })
  }, [chartingTab, persistChart])

  const { status: autoSaveStatus, triggerAutoSave } = useChartAutoSave({
    enabled: autoSaveEnabled,
    chartingTab,
    isDentalUpdated,
    isPerioUpdated,
    dentalSignature,
    perioSignature,
    canAutoSaveDental,
    canAutoSavePerio,
    onAutoSave: handleAutoSave,
  })

  const tryAutoSaveBeforePrompt = useCallback(async (): Promise<boolean> => {
    if (!autoSaveEnabled) {
      return false
    }
    if (hasTreatmentPlanUnsaved()) {
      const saved =
        (await treatmentPlanNavigationRef.current?.saveUnsavedChanges()) ??
        false
      if (!saved) {
        return false
      }
    }
    const saveChartTab = async (
      tab: 'home' | 'perio',
      isDirty: boolean,
      canSave: boolean,
    ): Promise<boolean> => {
      if (!isDirty) {
        return true
      }
      if (!canSave) {
        return false
      }
      if (tab === chartingTab) {
        return triggerAutoSave()
      }
      return persistChart({
        mode: 'autosave',
        chartingTab: tab,
        keepEditingSession: true,
        silent: true,
      })
    }
    const dentalSaved = await saveChartTab(
      'home',
      reduxStore.getState().charting.isDentalUpdated,
      canAutoSaveDental,
    )
    if (!dentalSaved) {
      return false
    }
    return saveChartTab(
      'perio',
      reduxStore.getState().charting.isPerioUpdated,
      canAutoSavePerio,
    )
  }, [
    autoSaveEnabled,
    canAutoSaveDental,
    canAutoSavePerio,
    chartingTab,
    hasTreatmentPlanUnsaved,
    persistChart,
    reduxStore,
    triggerAutoSave,
  ])

  const autoSaveCurrentTab = useCallback(async (): Promise<boolean> => {
    const { isDentalUpdated: dentalDirty, isPerioUpdated: perioDirty } =
      reduxStore.getState().charting
    const isDirty =
      chartingTab === 'home'
        ? dentalDirty
        : chartingTab === 'perio'
          ? perioDirty
          : false
    return isDirty ? triggerAutoSave() : true
  }, [chartingTab, reduxStore, triggerAutoSave])

  useEffect(() => {
    tryAutoSaveBeforePromptRef.current = tryAutoSaveBeforePrompt
    autoSaveCurrentTabRef.current = autoSaveCurrentTab
    autoSaveEnabledRef.current = autoSaveEnabled
  }, [tryAutoSaveBeforePrompt, autoSaveCurrentTab, autoSaveEnabled])

  useEffect(() => {
    return () => {
      if (autoSaveEnabled) {
        void triggerAutoSave()
      }
    }
  }, [autoSaveEnabled, triggerAutoSave])

  const handleSaveChart = (): void => {
    const isUpdated = chartingTab === 'home' ? isDentalUpdated : isPerioUpdated

    if (canFinishEditing && !isUpdated) {
      exitEditingSession(chartingTab)
      return
    }

    if (chartingTab === 'home') {
      if (!validateDentalFormForManualSave()) return
      setPendingSaveData(buildSaveConfirmation('dental'))
    } else if (chartingTab === 'perio') {
      if (!validatePerioFormForManualSave()) return
      setPendingSaveData(buildSaveConfirmation('perio'))
    } else {
      return
    }

    setShowSaveConfirm(true)
  }

  const confirmSaveChart = async (): Promise<void> => {
    if (!pendingSaveData) return
    const chartingTabToSave =
      pendingSaveData.type === 'dental' ? 'home' : 'perio'
    setPendingSaveData(null)
    setShowSaveConfirm(false)
    await persistChart({
      mode: 'manual',
      chartingTab: chartingTabToSave,
      keepEditingSession: false,
      silent: false,
    })
  }

  const dismissSaveConfirm = (): void => {
    setShowSaveConfirm(false)
    setPendingSaveData(null)
  }

  const discardAllUnsavedChanges = useCallback((): void => {
    if (hasTreatmentPlanUnsaved()) {
      treatmentPlanNavigationRef.current?.discardUnsavedChanges()
    }
    if (isDentalUpdated) {
      resetChartForTab('home')
    }
    if (isPerioUpdated) {
      resetChartForTab('perio')
    }
  }, [
    hasTreatmentPlanUnsaved,
    isDentalUpdated,
    isPerioUpdated,
    resetChartForTab,
  ])

  const handleUnsavedLeaveCancel = (): void => {
    setShowUnsavedLeaveConfirm(false)
    setPendingLeave(null)
  }

  const handleUnsavedLeaveDiscard = (): void => {
    discardAllUnsavedChanges()
    completePendingLeave()
  }

  return (
    <div className="charting-page h-100">
      <div
        className={`charting-loading-container charting-page-content${
          isPageReady ? ' is-ready' : ''
        }`}
      >
        {pagePreviousState && (
          <div className="charting-go-back">
            <LinkButton
              type="button"
              size="sm"
              underline={false}
              leftIcon={<ArrowLeftIcon width={16} height={16} aria-hidden />}
              onClick={handleGoBack}
            >
              {chartingBackLabel}
            </LinkButton>
          </div>
        )}
        <ChartingTopBar
          currentTab={chartingTab}
          onTabChange={handleChangeTab}
          layers={layers}
          onLayerChange={handleLayerChange}
          chartIndex={chartIndex}
          onChartToggle={handleChartToggle}
          isMicOn={isMicOn}
          onMicToggle={handleMicToggle}
          micDisabled={!voiceEnabled}
          micStatus={activeMicStatus}
          isFullscreen={isFullscreen}
          setIsFullscreen={setIsFullscreen}
          onAddChart={handleAddChart}
          onSaveChart={handleSaveChart}
          onResetChart={handleResetChart}
          autoSaveEnabled={autoSaveEnabled}
          autoSaveStatus={autoSaveStatus}
          canFinishEditing={canFinishEditing}
          onViewNote={stableOnViewNote}
          clearSelection={clearSelection}
          onAddTreatmentPlan={() => {
            if (addTreatmentPlanRef.current) {
              addTreatmentPlanRef.current()
            }
          }}
          isXrayPanelOpen={isXrayPanelOpen}
          onXrayPanelToggle={() => {
            // When closing the panel, drop any previously-loaded file(s) so
            // re-opening starts at the empty state (local/cloud chooser)
            // instead of auto-reloading the last DICOM.
            if (isXrayPanelOpen) {
              setInitialEmbeddedFiles(null)
            }
            dispatch(toggleXrayPanel())
          }}
        />

        <div className="charting-tab-stage">
          {showChartingSkeleton ? (
            <ChartingSkeleton variant={chartingTab} />
          ) : null}
          <div
            className={`charting-tab-body flex-grow-1 min-h-0 d-flex flex-column${
              isChartContentPending ? ' charting-tab-body--pending' : ''
            }`}
          >
            {!selectedPatient?.patient_id ? (
              <div className="charting-no-patient-empty-state">
                <EmptyState
                  className="charting-no-patient-empty"
                  title="No charting data to display"
                  description="Select a patient to view their charts."
                  actionLabel="Select a patient"
                  onAction={() => dispatch(requestPatientSelection())}
                />
              </div>
            ) : (
              <>
                <Routes>
                  <Route path="/" element={null} />
                  <Route path="perio" element={null} />
                  <Route path="treatment-plan" element={null} />
                  <Route
                    path="history"
                    element={
                      <HistoryPage
                        onViewNote={stableOnViewNote}
                        onGoToPlans={handleGoToPlans}
                      />
                    }
                  />
                </Routes>

                {dentalChartMounted ? (
                  <div
                    className={`charting-persistent-dental h-100${
                      chartingTab !== 'home'
                        ? ' charting-persistent-pane--inactive'
                        : ''
                    }`}
                    aria-hidden={chartingTab !== 'home'}
                  >
                    <ChartingMain
                      onViewNote={stableOnViewNote}
                      layers={layers}
                      isFullscreen={isFullscreen}
                      isXrayPanelOpen={isXrayPanelOpen}
                      gridRef={dentalHistoryGridRef}
                      validationErrors={dentalValidationErrors}
                      setValidationErrors={setDentalValidationErrors}
                      initialEmbeddedFiles={initialEmbeddedFiles}
                      chartBootstrapComplete={!isChartContentPending}
                      isHostVisible={
                        chartingTab === 'home' && !isChartContentPending
                      }
                      voiceStatus={<DentalVoiceStatus voice={dentalVoice} />}
                      voiceActive={
                        dentalVoiceActive || forceDentalVoiceAccordion
                      }
                      chartingInfoFocusTick={dentalChartingInfoFocusTick}
                      voiceTooth={dentalVoiceTooth}
                      onSelectedToothChange={handleDentalSelectedToothChange}
                    />
                  </div>
                ) : null}

                <div
                  className={`charting-persistent-perio h-100${
                    chartingTab !== 'perio'
                      ? ' charting-persistent-pane--inactive'
                      : ''
                  }`}
                  aria-hidden={chartingTab !== 'perio'}
                >
                  {shouldMountPerioTable(
                    perioTableVisitedRef.current,
                    chartingTab,
                  ) ? (
                    <PerioPage
                      voiceStatus={perioVoiceStatus}
                      voiceActive={perioVoiceActive || forcePerioVoiceAccordion}
                      chartingInfoFocusTick={perioChartingInfoFocusTick}
                      voiceMeasurement={perioVoiceMeasurement}
                      cursor={voice.keyword.cursor}
                      voiceHighlight={
                        isMicOn
                          ? (voice.viewCursor ?? voice.keyword.cursor)
                          : undefined
                      }
                      onCursor={stableSelectPerioCursor}
                      chartIndex={chartIndex}
                      onViewNote={stableOnViewNote}
                      updatePerioValue={stableUpdatePerioValue}
                      isFullscreen={isFullscreen}
                      gridRef={perioHistoryGridRef}
                      validationErrors={perioValidationErrors}
                      setValidationErrors={setPerioValidationErrors}
                    />
                  ) : null}
                </div>

                <div
                  className={`charting-persistent-treatment-plan h-100${
                    chartingTab !== 'treatment-plan'
                      ? ' charting-persistent-pane--inactive'
                      : ''
                  }${isChartContentPending ? ' charting-tab-body--pending' : ''}`}
                  aria-hidden={chartingTab !== 'treatment-plan'}
                >
                  <TreatmentPlanPage
                    onViewNote={stableOnViewNote}
                    onAddTreatmentPlanRef={addTreatmentPlanRef}
                    navigationRef={treatmentPlanNavigationRef}
                    isXrayPanelOpen={isXrayPanelOpen}
                    initialEmbeddedFiles={initialEmbeddedFiles}
                    isHostVisible={
                      chartingTab === 'treatment-plan' && !isChartContentPending
                    }
                  />
                </div>
              </>
            )}
          </div>
        </div>

        <Modal
          open={showSaveConfirm}
          onClose={dismissSaveConfirm}
          variant="confirmation"
          size="md"
          title={`Are you sure you want to ${
            pendingSaveData?.chartId === '' ? 'save' : 'update'
          } this chart?`}
          description={
            pendingSaveData?.titleResolution.wasRewritten
              ? `A chart named "${pendingSaveData.titleResolution.requestedTitle}" already exists. It will be saved as "${pendingSaveData.titleResolution.uniqueTitle}". Cancel if you want to choose a different title.`
              : undefined
          }
          primaryLabel={`Confirm ${
            pendingSaveData?.chartId === '' ? 'Save' : 'Update'
          }`}
          secondaryLabel="Cancel"
          onPrimaryClick={() => {
            void confirmSaveChart()
          }}
          onSecondaryClick={dismissSaveConfirm}
        >
          {pendingSaveData?.type === 'dental' &&
          pendingSaveData.dentalSummary ? (
            <DentalChartSummaryContent
              summary={pendingSaveData.dentalSummary}
            />
          ) : null}
        </Modal>
        <ChartingUnsavedChangesModal
          show={showUnsavedLeaveConfirm}
          unsavedTabs={showUnsavedLeaveConfirm ? getUnsavedTabLabels() : []}
          onKeepEditing={handleUnsavedLeaveCancel}
          onDiscard={handleUnsavedLeaveDiscard}
        />
        <Modal
          open={alertMessageInfo.show}
          onClose={() =>
            setAlertMessageInfo({ ...alertMessageInfo, show: false })
          }
          variant="notice"
          size="sm"
          iconTone={alertMessageInfo.type === 'error' ? 'danger' : 'success'}
          title={alertMessageInfo.title}
          description={alertMessageInfo.message}
          primaryLabel="Close"
          onPrimaryClick={() =>
            setAlertMessageInfo({ ...alertMessageInfo, show: false })
          }
        />
      </div>
    </div>
  )
}

export default ChartingPage
